import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  map,
  range,
  paths,
  t,
  styles,
  classMap,
} from '@hh/global.js'

class HHSlideshow extends LitElement {
  static properties = {
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    speed: {
      reflect: true,
      type: Number,
    },
    playing: {
      reflect: true,
      type: Boolean,
    },
    userPause: {
      type: Boolean,
    },
    autoplay: {
      state: true,
    },
    slideCount: {
      type: Number,
      attribute: 'slide-count',
    },
    strings: {
      type: Object,
    },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()
  $togglePlay = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1
    this.playing = false
    this.userPause = false
    this.speed = 0
    this.highlightCompleted = false

    this.touchstartX = 0
    this.touchendX = 0
    this.swipeThreshold = 50

    this.updateUI = this.updateUI.bind(this)
  }

  connectedCallback() {
    super.connectedCallback();
  
    this.items = Array.from(this.querySelectorAll('[slot="item"]'));
    this._total = this.items.length;
  
    this.speed = this.getAttribute('speed');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  
    if (!this.reducedMotion.matches && this.hasAttribute('use-video-rotation')) {
      this.setupVideoEndTrigger(this.items[this._current - 1]);
    } else if (this.hasAttribute('speed')) {
      this.setAutoplay();
    } else {
      this.autoplay = false;
      this.userPause = true;
    }
    
    
  
    if (this.hasAttribute('use-video-rotation')) {
      const initialVideoSlide = this.items[this._current - 1];
      this.setupVideoEndTrigger(initialVideoSlide);
    }
  
    window.addEventListener('keydown', this.onModalKeydown.bind(this));
  }
  

  disconnectedCallback() {
    super.disconnectedCallback();
  
    const currentSlide = this.items?.[this._current - 1];
    const video = currentSlide?.querySelector?.('video');
    if (video) {
      video.onended = null;
    }
  
    clearTimeout(this.imageSlideTimeout);
  
    window.removeEventListener('keydown', this.onModalKeydown);
  }
  
  

  firstUpdated() {
    this.$container?.value?.addEventListener('scroll', this.updateUI)
    this.$container?.value?.addEventListener(
      'touchstart',
      this.onTouchStart.bind(this),
      {
        passive: true,
      }
    )
    this.$container?.value?.addEventListener(
      'touchend',
      this.onTouchEnd.bind(this),
      {
        passive: true,
      }
    )
  }

  updated(changedProperties) {
    if (changedProperties.has('_current')) {
      this.setSlideVisibility()
    }
  }

  setSlideVisibility() {
    this.items.forEach((item, idx) => {
      const linkElements = item.querySelectorAll('hh-button')
      const idxOffset = idx + 1
      if (idxOffset === this._current) {
        if (linkElements.length) {
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex')
          })
          item.setAttribute('aria-hidden', 'false')
          item.removeAttribute('tabindex')
        }
      } else {
        if (linkElements.length) {
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1')
          })
        }
        item.setAttribute('aria-hidden', 'true')
        item.setAttribute('tabindex', '-1')
      }
    })
  }

  updateUI() {
    if (!this._total) return;
  
    const newIndex = Math.round(
      this.$container?.value?.scrollLeft / this.items[0].clientWidth
    ) + 1;
  
    if (newIndex !== this._current) {
      this._current = newIndex;
  
      const currentSlide = this.items[this._current - 1];
      const video = currentSlide?.querySelector?.('video');
  
      if (video) {
        // Restart the video if it was at the end
        if (video.ended) {
          try {
            video.currentTime = 0;
            video.play().catch(() => {});
          } catch (_) {}
        }
  
        if (this.hasAttribute('use-video-rotation')) {
          this.setupVideoEndTrigger(currentSlide);
        }
      }
    }
  
    this._current === 1
      ? this.$prev?.value?.setAttribute('disabled', true)
      : this.$prev?.value?.removeAttribute('disabled');
  
    this._current === this._total
      ? this.$next?.value?.setAttribute('disabled', true)
      : this.$next?.value?.removeAttribute('disabled');
  }
  
  

  move(event) {
    event.preventDefault()
    this.userPause = true
    const slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.$container?.value?.scrollLeft + this.items[0].clientWidth
        : this.$container?.value?.scrollLeft - this.items[0].clientWidth
    this.$container?.value?.scrollTo({
      left: slideScrollPosition,
    })
  }

  moveTo(slide) {
    const slideScrollPosition = this.items[slide].offsetLeft;
    this.$container?.value?.scrollTo({
      left: slideScrollPosition,
    });
  
    const nextSlide = this.items[slide];
  
    // Restart video if present
    const video = nextSlide.querySelector('video');
    if (video) {
      try {
        video.currentTime = 0;
        video.play().catch(() => {});
      } catch (_) {}
    }
  
    const nextSlideImages = nextSlide.querySelectorAll('img');
    this.eagerLoadSlidesImages(nextSlideImages);
  }
  

  eagerLoadSlidesImages(images) {
    images.forEach((img) => {
      img.setAttribute('loading', 'eager')
    })
  }

  setupVideoEndTrigger(slide) {
    if (!this.hasAttribute('use-video-rotation')) return;
  
    clearTimeout(this.imageSlideTimeout);
    this.playing = true;
    this.userPause = false;
  
    const videos = slide.querySelectorAll('video');
  
    if (videos.length > 0) {
      videos.forEach((video) => {
        video.onended = null;
  
        const updateSpeedFromVideo = () => {
          const duration = video.duration || 5;
          this.style.setProperty('--slideshow-speed', `${duration}s`);
        };
  
        video.addEventListener('loadedmetadata', updateSpeedFromVideo, { once: true });
  
        try {
          video.currentTime = 0;
          video.play().catch(() => {});
        } catch (_) {}
  
        video.onended = () => {
          this.highlightCompleted = true;
          this.requestUpdate();
          this.rotateSlides();
        };
      });
    } else {
      const imageDelay = parseInt(this.getAttribute('speed'), 10) || 5000;
      this.style.setProperty('--slideshow-speed', `${imageDelay / 1000}s`);
  
      this.imageSlideTimeout = setTimeout(() => {
        this.highlightCompleted = true;
        this.requestUpdate();
        this.rotateSlides();
      }, imageDelay);
    }
  }
  
   

  rotateSlides() {
    clearTimeout(this.imageSlideTimeout);
  
    const currentSlide = this.items[this._current - 1];
    const oldVideos = currentSlide?.querySelectorAll?.('video') || [];
    oldVideos.forEach((video) => (video.onended = null));
  
    const isLastSlide = this._current === this._total;
    const nextIndex = isLastSlide ? 0 : this._current;
  
    const slideScrollPosition = this.items[nextIndex].offsetLeft;
    this.$container?.value?.scrollTo({ left: slideScrollPosition });
  
    const nextSlide = this.items[nextIndex];
    const videos = nextSlide.querySelectorAll('video');
  
    if (videos.length > 0) {
      videos.forEach((video) => {
        try {
          video.currentTime = 0;
          video.play().catch(() => {});
        } catch (_) {}
      });
    }
  
    this.setupVideoEndTrigger(nextSlide);
  
    if (videos.length === 0) {
      const nextSlideImages = nextSlide.querySelectorAll('img');
      this.eagerLoadSlidesImages(nextSlideImages);
    }
  }
  
  
  

  play() {
    this.$container?.value?.setAttribute('aria-live', 'off')
    this.playing = true
  }

  pause() {
    this.$container?.value?.setAttribute('aria-live', 'polite')
    this.playing = false
  }

  setAutoplay() {
    if (!this.speed || this.hasAttribute('use-video-rotation')) return;
  
    this.style.setProperty('--slideshow-speed', `${this.speed / 1000}s`);
  
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.rotateSlides.bind(this), this.speed);
    this.play();
  }
  
  

  stopAutoplay() {
    // setInterval option
    clearInterval(this.autoplay)

    // IntervalTimer option
    // this.autoplay.pause()

    this.pause()
  }

  toggleAutoplay() {
    if (this.hasAttribute('use-video-rotation')) return; 

    if (this.playing) {
      this.userPause = true;
      this.stopAutoplay();
    } else {
      this.userPause = false;
      this.setAutoplay();
    }

  }

  // onFocusIn() {
  //   if (this.userPause || this.reducedMotion.matches) return
  //   this.stopAutoplay()
  // }

  // onFocusOut() {
  //   if (this.userPause || this.reducedMotion.matches) return
  //   this.setAutoplay()
  // }

  onPaginationClick(e, slideIndex) {
    e.preventDefault()
    this.userPause = true
    this.stopAutoplay()
    this.moveTo(slideIndex)
  }

  onTouchStart(e) {
    this.touchstartX = e.changedTouches[0].screenX
  }

  onTouchEnd(e) {
    this.touchendX = e.changedTouches[0].screenX
    this.checkSwipe()
  }

  checkSwipe() {
    if (
      this.touchendX < this.touchstartX - this.swipeThreshold ||
      this.touchendX > this.touchstartX + this.swipeThreshold
    ) {
      this.userPause = true
      this.stopAutoplay()
    }
  }

  // Video Modal
  onVideoLinkClick(e) {
    e.preventDefault()
    scrollLock.disablePageScroll(this.videoModal)

    const videoUrl = e.target.getAttribute('video-url')
    const videoPoster = e.target.getAttribute('video-poster')
    const youtubeId = e.target.getAttribute('youtube-id')

    this.userPause = true
    this.stopAutoplay()

    if (videoUrl) {
      this.videoModal.innerHTML = this.vdsTemplate(videoUrl, videoPoster)
      const videoHTML = this.videoModal.querySelector('video')
      videoHTML.addEventListener('canplay', () => {
        videoHTML.play()
      })
    } else if (youtubeId) {
      this.videoModal.innerHTML = this.youtubeTemplate(youtubeId)
      const youtubeHTML = this.videoModal.querySelector('iframe')
      youtubeHTML.addEventListener('load', () => {
        youtubeHTML.contentWindow.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          '*'
        )
      })
    } else {
      console.log('No video URL or YouTube ID found')
    }
  }

  onModalClose() {
    scrollLock.enablePageScroll(this.videoModal)
    this.videoModal.innerHTML = ''
    this.userPause = false
    this.setAutoplay()
  }

  onModalClickOutside(e) {
    if (e.target !== this.hhModal) return
    this.hhModal.close(e)
    this.onModalClose()
  }

  onModalKeydown(e) {
    if (e.code.toUpperCase() !== 'ESCAPE') return
    this.hhModal.close(e)
    this.onModalClose()
  }

  get videoModal() {
    return this.querySelector('.js-video-modal') ?? null
  }

  get hhModal() {
    return this.querySelector('hh-modal') ?? null
  }

  vdsTemplate(source, poster = '') {
    if (poster) {
      return `<video controls playsinline src="${source}" poster="${poster}"></video>`
    } else {
      return `<video controls playsinline src="${source}"></video>`
    }

    // TODO: Come back to this if we want to use vidstack
    // Vidstack limitation: loading dynamic source since none of the events seem to trigger.
    return `
      <vds-media controls view="video">
        <vds-video>
          <video controls src="${source}"></video>
        </vds-video>
      </vds-media>
    `
  }

  youtubeTemplate(id) {
    return `
      <iframe
        src="https://www.youtube.com/embed/${id}?enablejsapi=1&playlist=${id}&disablekb=0&fs=1&iv_load_policy=1&modestbranding=1&rel=1&widgetid=1"
        frameborder="0"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowfullscreen
      ></iframe>
    `
  }

  render() {
    return html`<!-- @ts-ignore no-incompatible-type-binding -->
      <div>
        <div
          class="container"
          ${ref(this.$container)}
          @mouseenter="${this.onFocusIn}"
          @mouseleave="${this.onFocusOut}"
          @focusin="${this.onFocusIn}"
          @focusout="${this.onFocusOut}"
        >
          <slot name="item" @openVideoModal=${this.onVideoLinkClick}></slot>
        </div>
        <div class="controls">
          <div
            class="${classMap({
              pagination: true,
              hidden: this.slideCount === 1,
            })}"
          >
            ${map(range(this.slideCount), (idx) => {
              return html`
                <button
                  class="pagination-button"
                  aria-label="Go to slide ${idx + 1}"
                  aria-current="${this._current === idx + 1}"
                  @click="${(e) => this.onPaginationClick(e, idx)}"
                >
                  <span class="pagination-button-inner">
                    <span
                      data-paginate-progress
                      class="pagination-button-progress ${classMap({
                        animation: this._current === idx + 1 && !this.userPause,
                        pause: !this.playing,
                        selected: this._current === idx + 1 && this.userPause,
                        completed:
                          this.highlightCompleted && this._current > idx + 1,
                      })}"
                    ></span>
                  </span>
                </button>
              `
            })}
          </div>

          <button
            class="${classMap({
              toggle: true,
              hidden: !this.speed || this.slideCount === 1,
            })}"
            @click=${this.toggleAutoplay}
            aria-label=${this.playing ? this.strings.pause : this.strings.play}
          >
            <span
              class="${classMap({
                hidden: this.playing,
              })}"
              >${unsafeHTML(paths.play)}</span
            >
            <span
              class="${classMap({
                hidden: !this.playing,
              })}"
              >${unsafeHTML(paths.pause)}</span
            >
          </button>
        </div>

        <div class="buttons visually-hidden">
          <div class="counter">
            <span class="counter--current">${this._current}</span>
            <span aria-hidden="true"> / </span>
            <span class="visually-hidden">${t('accessibility.of', 'of')}</span>
            <span class="counter--total"
              >${this._total ? this._total : ''}</span
            >
          </div>
          <button
            ${ref(this.$prev)}
            @click="${this.move}"
            type="button"
            class="button button--prev"
            name="previous"
            aria-label="${t('accessibility.previous_slide', 'Previous')}"
          >
            ${unsafeHTML(paths.caret)}
          </button>
          <button
            ${ref(this.$next)}
            @click="${this.move}"
            type="button"
            class="button button--next"
            name="next"
            aria-label="${t('accessibility.next_slide', 'Next')}"
          >
            ${unsafeHTML(paths.caret)}
          </button>
        </div>
      </div>

      <slot
        name="video-modal"
        @modal-close=${this.onModalClose}
        @click=${this.onModalClickOutside}
        @keydown=${this.onModalKeydown}
      ></slot> `
  }

  // prettier-ignore
  static styles = [
    styles.visuallyHidden,
    css`
      :host {
        box-sizing: border-box;
        position: relative;
        overflow: hidden;
      }

      *,
      *::before,
      *::after {
        box-sizing: inherit;
      }

      .container {
        width: var(--hh-container-width, 100%);
        display: var(--hh-container-flex);
        flex-wrap: var(--hh-container-flex-wrap);
        margin: var(--hh-container-margin);
      }

      .container {
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        background: var(--hh-background);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
      }

      .container::-webkit-scrollbar {
        display: none;
      }

      .controls {
        position: absolute;
        padding: 0 1rem;
        bottom: 0;
        display: flex;
        align-items: baseline;
        column-gap: 1rem;
        width: calc(100% - 2rem);
      }

      .pagination {
        display: flex;
        width: 100%;
        justify-content: space-between;
        column-gap: 1rem;
      }

      .toggle {
        width: 2.4rem;
        height: 2.4rem;
        padding: 0;
        color: var(--hh-color-background);
        background-color: transparent;
        border: none;
        cursor: pointer;
      }

      .toggle svg {
        width: 100%;
        height: 100%;
      }

      .hidden {
        display: none;
      }

      .buttons {
        align-items: center;
        position: absolute;
        z-index: 2;
        right: 0;
        bottom: 0;
        border: var(--hh-button-border);
        background: var(--hh-button-background);
      }

      .counter {
        margin: 0 2rem;
      }

      .button {
        width: var(--hh-button-width, 44px);
        height: var(--hh-button-height, 44px);
        cursor: pointer;
        color: var(--hh-color);
        border: none;
        background: var(--hh-button-background);
      }

      .button svg {
        height: 0.6rem;
      }

      .button--prev {
        margin-left: -0.2rem;
        transform: rotate(90deg);
      }

      .button--next {
        margin-left: -0.2rem;
        transform: rotate(-90deg);
      }

      .pagination-button {
        width: 100%;
        padding: 2rem 0 1rem;
        background: transparent;
        border: none;
        cursor: pointer;
      }

      .pagination-button-inner {
        position: relative;
        display: block;
        background: rgba(255, 255, 255, 0.7);
        height: 0.2rem;
      }

      .pagination-button-progress {
        position: absolute;
        inset: 0;
        display: block;
        background: red;
        height: 0.2rem;
        width: 0;
      }

      .pagination-button-progress.animation {
        animation: slideProgress var(--slideshow-speed) linear 1 forwards;
      }

      .pagination-button-progress.completed,
      .pagination-button-progress.selected {
        width: 100%;
      }

      .pagination-button-progress.incomplete {
        width: 0;
      }

      .pagination-button-progress.pause {
        animation-play-state: paused;
      }

      @keyframes slideProgress {
        from {
          width: 0;
        }
        to {
          width: 100%;
        }
      }
    `,
  ]
}

customElements.define('hh-slideshow', HHSlideshow)
